import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { CATEGORY_IMAGES, HERO_BG } from "@/data/gameData";
import { ArrowLeft, Dices, Loader2, CheckCircle2, Sparkles, LayoutGrid } from "lucide-react";

export default function Categories() {
  const { http } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("destaque");
  const [cats, setCats] = useState([]);
  const [stories, setStories] = useState([]);
  const [completed, setCompleted] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([http.get("/categories"), http.get("/stories"), http.get("/me/history")])
      .then(([c, s, h]) => {
        setCats(c.data);
        setStories(s.data);
        const map = {};
        (h.data || []).forEach((r) => { map[r.category] = (map[r.category] || 0) + 1; });
        setCompleted(map);
      })
      .catch(() => {}).finally(() => setLoading(false));
  }, [http]);

  const doneCount = stories.filter((s) => s.completed).length;
  const sortedStories = [...stories].sort(
    (a, b) => ((b.novo && !b.completed) ? 1 : 0) - ((a.novo && !a.completed) ? 1 : 0)
  );

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-5xl mx-auto px-5 py-8">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <h1 className="font-display font-black text-4xl tracking-tighter">Histórias</h1>
        <p className="text-slate-400 mt-2">
          {stories.length} histórias em destaque + {cats.length} categorias. Cada partida é gerada de forma única.
        </p>

        {/* tabs */}
        <div className="flex gap-1 p-1 bg-white/5 rounded-full mt-6 max-w-md">
          {[
            { k: "destaque", label: "Em destaque", icon: Sparkles },
            { k: "categorias", label: "Por categoria", icon: LayoutGrid },
          ].map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === t.k ? "bg-[#F43F5E] text-white" : "text-slate-400 hover:text-white"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F43F5E]" /></div>
        ) : tab === "destaque" ? (
          <>
            <p className="text-xs text-slate-500 mt-6">{doneCount} de {stories.length} concluídas</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mt-3">
              {sortedStories.map((s, i) => (
                <motion.button key={s.id} data-testid={`story-${s.id}`}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % 9) * 0.04 }}
                  onClick={() => navigate(`/jogar/${s.category}?story=${s.id}`)}
                  className="group relative text-left rounded-3xl p-5 bg-[#0F0F14] border border-white/10 hover:border-[#F43F5E]/40 hover:-translate-y-1 transition-all overflow-hidden">
                  <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-opacity" style={{ background: s.cor }} />
                  <div className="flex items-start justify-between">
                    <span className="text-3xl">{s.emoji}</span>
                    <div className="flex items-center gap-1.5">
                      {s.novo && !s.completed && (
                        <span data-testid={`story-new-${s.id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FACC15] text-black shadow-[0_0_12px_rgba(250,204,21,0.5)]">
                          Novo
                        </span>
                      )}
                      {s.completed && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
                    </div>
                  </div>
                  <p className="font-display font-black text-lg tracking-tight mt-3 leading-tight">{s.titulo}</p>
                  <p className="text-xs mt-1.5 inline-flex items-center gap-1.5" style={{ color: s.cor }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.cor }} /> {s.categoria_nome}
                  </p>
                  <p className="text-sm text-slate-400 mt-2 line-clamp-2">{s.premissa}</p>
                </motion.button>
              ))}
            </div>
          </>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-6">
            {cats.map((c, i) => (
              <motion.button key={c.id} data-testid={`category-${c.id}`}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % 9) * 0.04 }}
                onClick={() => navigate(`/jogar/${c.id}`)}
                className="group relative h-40 rounded-3xl overflow-hidden border border-white/10 text-left">
                <img src={CATEGORY_IMAGES[c.id] || HERO_BG} alt={c.nome}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
                <div className="absolute bottom-0 left-0 p-5">
                  <span className="text-2xl">{c.emoji}</span>
                  <p className="font-display font-black text-xl tracking-tight mt-1" style={{ color: "#fff" }}>{c.nome}</p>
                </div>
                {completed[c.id] ? (
                  <div className="absolute top-3 left-3 flex items-center gap-1 text-[11px] px-2 py-1 rounded-full glass border border-white/15 text-emerald-300">
                    <CheckCircle2 className="w-3 h-3" /> {completed[c.id]}x
                  </div>
                ) : null}
                <div className="absolute top-4 right-4 w-2.5 h-2.5 rounded-full" style={{ background: c.cor, boxShadow: `0 0 12px ${c.cor}` }} />
              </motion.button>
            ))}
          </div>
        )}

        <button data-testid="surprise-btn" onClick={() => navigate("/jogar/surpresa")}
          className="mt-6 w-full flex items-center justify-center gap-2 rounded-2xl py-4 bg-[#06B6D4]/15 border border-[#06B6D4]/30 text-[#67E8F9] font-semibold hover:bg-[#06B6D4]/25 transition-colors">
          <Dices className="w-5 h-5" /> Surpreenda-me
        </button>
      </div>
    </div>
  );
}
